import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PropertyRating {
  average: number;
  count: number;
}

/**
 * Fetches published review ratings for every property in one round-trip and
 * aggregates them per property. Cards read from this shared cache instead of
 * firing one query each.
 */
export const usePropertyRatings = () =>
  useQuery({
    queryKey: ["property-ratings-map"],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Record<string, PropertyRating>> => {
      const { data, error } = await supabase
        .from("reviews")
        .select("property_id, rating")
        .eq("status", "published")
        .not("property_id", "is", null);
      if (error) throw error;

      const sums: Record<string, { sum: number; rated: number; total: number }> = {};
      for (const row of data ?? []) {
        const id = row.property_id as string | null;
        if (!id) continue;
        if (!sums[id]) sums[id] = { sum: 0, rated: 0, total: 0 };
        sums[id].total += 1;
        if (typeof row.rating === "number") {
          sums[id].sum += row.rating;
          sums[id].rated += 1;
        }
      }

      const out: Record<string, PropertyRating> = {};
      for (const [id, v] of Object.entries(sums)) {
        if (!v.rated) continue;
        out[id] = { average: v.sum / v.rated, count: v.total };
      }
      return out;
    },
  });
